import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { LinkedinLogo, Envelope, MapPin, Globe, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import VynexLogo from "./VynexLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAP_BG =
  "https://static.prod-images.emergentagent.com/jobs/f7842974-09b3-4b3e-bebd-5a0c602980d0/images/c20eae420d29f9b75246b3a82a6a8a3ecdbeb13f21d9b52d264b2b71e5897254.png";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    subject: "General Enquiry",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    
    {/* - Old Code from Git Hub
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. Our team will respond shortly.");
      setForm({
        name: "",
        email: "",
        company: "",
        subject: "General Enquiry",
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Unable to send message. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    } 
  */}

    // Added for Netlify
    const encodedData = new URLSearchParams({
      "form-name": "contact",
      ...form
    }).toString();

    {/*
    try {
    const response = await fetch("https://vynexglobal.sg", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encodedData, //   Now handled entirely by Netlify
    });
    */}

    try {
      // 🚀 Make sure your unique Formspree ID code is listed below:
      const response = await fetch("https://formspree.io/f/mredyjpq", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(form), 
      });

    if (response.ok) {
      toast.success("Message received. Our team will respond to you shortly.");
      setForm({
          name: "",
          email: "",
          company: "",
          subject: "General Enquiry",
          message: "",
        });
    } else {
      throw new Error("Form submission failed");
    }
  } catch (err) {
    toast.error("Unable to send message. Please try again.");
  } finally {
      setSubmitting(false);
            } 
  // Added for Netlify
  };

  return (
    <section
      id="contact"
      className="relative bg-vynex-navy text-white pt-24 lg:pt-32 overflow-hidden"
      data-testid="contact-section"
    >
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `url(${MAP_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "invert(1)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left - Info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-vynex-gold" />
              <span className="eyebrow text-vynex-gold">Get in Touch</span>
            </div>
            <h2 className="font-display text-4xl lg:text-[56px] leading-[1.05] tracking-tight">
              Let&rsquo;s build a{" "}
              <span className="italic text-vynex-silver">
                long-term partnership.
              </span>
            </h2>
            <p className="mt-6 text-vynex-silver/70 leading-relaxed max-w-md">
              Whether you&rsquo;re a supplier, buyer, or strategic partner —
              reach out and our team will respond with discretion.
            </p>

            <div className="mt-12 space-y-7">
              <div className="flex items-start gap-4">
                <MapPin size={22} weight="thin" className="text-vynex-gold mt-1" />
                <div>
                  <div className="eyebrow text-vynex-silver/60 mb-1">
                    Office
                  </div>
                  <div className="text-white text-lg">
                    Vynex Global Pte Ltd
                  </div>
                  <div className="text-vynex-silver/70">Singapore</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Envelope size={22} weight="thin" className="text-vynex-gold mt-1" />
                <div>
                  <div className="eyebrow text-vynex-silver/60 mb-1">Email</div>
                  <a
                    href="mailto:info@vynexglobal.sg"
                    className="text-white text-lg hover:text-vynex-gold transition-colors"
                    data-testid="contact-email"
                  >
                    info@vynexglobal.sg
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Globe size={22} weight="thin" className="text-vynex-gold mt-1" />
                <div>
                  <div className="eyebrow text-vynex-silver/60 mb-1">Website</div>
                  <a
                    href="https://www.vynexglobal.sg"
                    target="_blank"
                    rel="noreferrer"
                    className="text-white text-lg hover:text-vynex-gold transition-colors"
                  >
                    www.vynexglobal.sg
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-12 flex items-center gap-4">
              <a
                href="https://www.linkedin.com"
                target="_blank"
                rel="noreferrer"
                className="w-11 h-11 border border-white/20 flex items-center justify-center hover:bg-vynex-gold hover:border-vynex-gold hover:text-vynex-navy transition-all"
                data-testid="linkedin-link"
              >
                <LinkedinLogo size={18} weight="bold" />
              </a>
              <span className="eyebrow text-vynex-silver/50">Follow</span>
            </div>
          </div>

          {/* Right - Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            onSubmit={submit}
            name="contact"         // Added for Netlify
            method="POST"          // Added for Netlify
            data-netlify="true"    // Added for Netlify
            className="lg:col-span-7 bg-white/[0.03] border border-white/10 p-8 lg:p-12 backdrop-blur-sm"
            data-testid="contact-form"
          >

          {/* Hidden verification element required for Netlify */}
          <input type="hidden" name="form-name" value="Contact" />

            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Full Name" required>
                <input
                  type="text"
                  name="name"  // Added for Netlify
                  value={form.name}
                  onChange={update("name")}
                  className="form-input"
                  required
                  data-testid="contact-input-name"
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email"
                  name="email"  // Added for Netlify 
                  value={form.email}
                  onChange={update("email")}
                  className="form-input"
                  required
                  data-testid="contact-input-email"
                />
              </Field>
              <Field label="Company">
                <input
                  type="text"
                  name="company"  // Added for Netlify
                  value={form.company}
                  onChange={update("company")}
                  className="form-input"
                  data-testid="contact-input-company"
                />
              </Field>
              <Field label="Subject">
                <select
                  name="message"  // Added for Netlify
                  value={form.subject}
                  onChange={update("subject")}
                  className="form-input"
                  data-testid="contact-input-subject"
                >
                  <option>General Enquiry</option>
                  <option>Sourcing Request</option>
                  <option>Supply Offer</option>
                  <option>Logistics</option>
                  <option>Partnership</option>
                </select>
              </Field>
            </div>
            <div className="mt-6">
              <Field label="Message" required>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className="form-input resize-none"
                  required
                  data-testid="contact-input-message"
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="mt-8 inline-flex items-center gap-3 bg-vynex-gold text-vynex-navy px-8 py-4 text-xs tracking-[0.25em] uppercase font-medium hover:bg-vynex-gold-light disabled:opacity-60 transition-colors"
              data-testid="contact-submit-button"
            >
              <PaperPlaneTilt size={16} weight="bold" />
              {submitting ? "Sending..." : "Send Enquiry"}
            </button>

            <style>{`
              .form-input {
                width: 100%;
                background: transparent;
                border: 0;
                border-bottom: 1px solid rgba(217,222,229,0.25);
                color: #ffffff;
                padding: 12px 0;
                outline: none;
                font-family: "IBM Plex Sans", sans-serif;
                font-size: 15px;
                transition: border-color 0.3s;
              }
              .form-input:focus { border-color: #c99a3a; }
              .form-input::placeholder { color: rgba(217,222,229,0.4); }
              .form-input option { color: #061b33; }
            `}</style>
          </motion.form>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative border-t border-white/10 bg-vynex-navy-deep">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <VynexLogo size={32} variant="light" />
            <div className="text-vynex-silver/80 text-sm">
              © {new Date().getFullYear()} Vynex Global Pte Ltd · Singapore
            </div>
          </div>
          <div className="flex items-center gap-6 eyebrow text-vynex-silver/50">
            <span>Singapore HQ</span>
            <span className="w-1 h-1 bg-vynex-gold rounded-full" />
            <span>Global Commodities</span>
            <span className="w-1 h-1 bg-vynex-gold rounded-full" />
            <span>Est. Trade</span>
          </div>
        </div>
      </footer>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="eyebrow text-vynex-silver/60 block mb-2">
      {label}
      {required && <span className="text-vynex-gold ml-1">*</span>}
    </span>
    {children}
  </label>
);

export default Contact;
